import { logger } from '@/lib/logger';
import { ScheduleType } from '@/types/gql.generated';
import { type ScheduleView, ScheduleViews } from '../types';
import type { DecoratedViewModule, ViewModule } from './types';

type Params = {
  viewModules: DecoratedViewModule[];
  isMobileBreakpoint: boolean;
  queryParams: URLSearchParams;
  scheduleType: ScheduleType;
  lastSelectedView?: ScheduleView;
};

const DEFAULT_VIEWS_BY_TYPE: Record<ScheduleType, ScheduleView> = {
  CLASSIC: 'week',
  LIST: 'week',
  AVAILABILITY: 'conflict',
  CONFLICT: 'conflict',
  MAGIC: 'week',
};

const isScheduleView = (view: unknown): view is ScheduleView => {
  return (
    typeof view === 'string' && ScheduleViews.includes(view as ScheduleView)
  );
};

export const getActiveViewModule = ({
  viewModules,
  queryParams,
  scheduleType,
  isMobileBreakpoint,
  lastSelectedView,
}: Params): ViewModule => {
  const defaultView = DEFAULT_VIEWS_BY_TYPE[scheduleType];
  const initialView = lastSelectedView || defaultView;
  const requestedView = queryParams.get('view');
  const attemptedView = isScheduleView(requestedView)
    ? requestedView
    : defaultView;

  let attemptedViewModule = viewModules.find(
    (viewModule) => viewModule.id === attemptedView && viewModule.isEnabled
  );

  if (!attemptedViewModule) {
    attemptedViewModule = viewModules.find(
      (viewModule) => viewModule.id === initialView
    );
  }

  if (!attemptedViewModule && initialView !== defaultView) {
    attemptedViewModule = viewModules.find(
      (viewModule) => viewModule.id === defaultView
    );
  }

  if (!attemptedViewModule) {
    logger.warn(
      `Couldn't find view module for schedule type=${scheduleType}, requested=${attemptedView}`
    );

    // Reached when none of the attempted/initial/default views resolve to a
    // module — e.g. the schedule's type has no entry in DEFAULT_VIEWS_BY_TYPE
    // (the server can roll out a new ScheduleType before the client knows about
    // it) and neither the URL nor the last-selected view names an available
    // module. Fall back to the first enabled module (or the first module) so
    // the schedule still renders instead of crashing the page (Sentry
    // WEB-CLIENT-256).
    attemptedViewModule =
      viewModules.find((viewModule) => viewModule.isEnabled) ?? viewModules[0];
  }

  if (!attemptedViewModule) {
    // Only reachable when there are no view modules at all — nothing to render.
    throw new Error('Unable to determine view');
  }

  if (isMobileBreakpoint && attemptedViewModule.mobileViewModule) {
    return attemptedViewModule.mobileViewModule;
  }

  return attemptedViewModule;
};

type WithOptionalProps<T, K extends keyof T> = Omit<T, K> & Partial<Pick<T, K>>;

export const createViewModule = (
  config: WithOptionalProps<
    ViewModule,
    | 'heroEnabled'
    | 'bannerEnabled'
    | 'actionBarEnabled'
    | 'fetchMessagesWithEntries'
    | 'scheduleTypes'
  >
): ViewModule => ({
  bannerEnabled: true,
  heroEnabled: true,
  actionBarEnabled: true,
  fetchMessagesWithEntries: false,
  scheduleTypes: [ScheduleType.Classic, ScheduleType.List],
  ...config,
});
